import { getReasonPhrase } from 'http-status-codes';
import URI from 'urijs';

const cache = {};

/**
 * Load some data.
 *
 * Use the new method.
 *
 * @param {Object} params
 * @param {String} params.url  The endpoint to pull data from.
 *
 * @param {String} params.method
 *      GET | POST | PUT | DELETE
 *
 * @param {String} params.documentType
 *      'json'
 *
 * @param {Function} params.success
 *
 * @param {Function || false} params.success
 *      Called if no error was encountered.  Arguments:
 *          {Object} response       The JSON response converted to an object.
 *          {XMLHttpRequest} xhr    The ajax request object.
 *          {Function} next         Return flow back to ajax() (if your function is an async).
 *
 *      If success is an async function then return true to let ajax() know that you will call
 *      next() when finished. Otherwise return an options object or false.
 *
 *      Values for options are:
 *
 *          {String|window.location} options.redirect
 *              To reload the page return window.location, otherwise return a string to navigate
 *              to another page.
 *
 *          {Object|False} options.notify
 *              Return false to prevent errors from being displayed.
 *              Return an object with override values to display a custom notification.
 *
 * @param {Function || false} params.failure
 *      Called if an error was encountered.  Arguments:
 *          {Object} response       The JSON response converted to an object.
 *          {XMLHttpRequest} xhr    The ajax request object.
 *          {Object} notify         Properties: title, text, value
 *          {Function} next         Return flow back to ajax() (if your function is an async).
 *
 *      If success is an async function then return true to let ajax() know that you will call
 *      next() when finished. Otherwise return an options object or false.
 *
 *      Refer to success() for controlling ajax()'s behaviour.
 *
 * @param {Function} params.callback
 *      Called after success()/failure() have returned.
 *      Use this method to contain cleanup code that relate to both success/failure.
 *
 * @param {Function} cb
 *      Same as params.callback
 */
window.ajax = ajax;
export default function ajax(params, cb) {
    const startCallback         = params.startCallback || (() => {});
    const progressHandler       = params.progressCallback || (() => {});
    const successCallback       = params.success;
    const failureCallback       = params.failure;
    const callback              = params.callback || cb;
    const method                = params.method || 'GET';
    const { data }              = params;
    const persist               = params.persist || 0;
    const { type }              = params;
    const query                 = params.query || {};
    const now                   = Date.now();
    const csrfInput             = document.getElementById('_csrf');

    document.dispatchEvent(new CustomEvent('loader.activate', { bubbles: true }));

    let url = params.url.indexOf('http') === -1
        ? util.urlBase() + params.url
        : params.url;

    if (url === '//') return console.warn('Missing url');

    url = URI(url).addQuery(query).toString();

    let response = cache[url];

    if (response && now < response.expires) {
        return finished(response);
    }

    if (params.bypassProxy) url = URI(url).addQuery({ _pb: Date.now() }).toString();

    const xhr = new XMLHttpRequest();

    xhr.addEventListener('loadend', loadEndHandler);
    xhr.addEventListener('progress', progressHandler);
    xhr.open(method, url);
    params.documentType = params.documentType || 'document';
    xhr.responseType = params.documentType;
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

    if (type === 'json') {
        xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
    }

    if (method.toUpperCase() !== 'GET' && csrfInput) { // Add CSRF token if possible/necessary
        xhr.setRequestHeader('X-CSRF-Token', csrfInput.value);
    }

    startCallback();

    if (params.formData) {
        xhr.send(params.formData);
    } else {
        xhr.send(JSON.stringify(data));
    }

    function loadEndHandler() {
        document.dispatchEvent(new CustomEvent('loader.deactivate', { bubbles: true }));
        // xhr = e.target;
        response = xhr.response;

        if (params.documentType === 'json' && xhr.responseType === '') { // MSIE doesn't support responseType=json
            try {
                response = JSON.parse(response);
            } catch (ex) {
                console.error(ex);
                return done(true, response);
            }
        }

        if (xhr.status !== 200) {
            return done(true, response); // let finished() deal with the error
        }

        if (params.documentType === 'document') {
            response.expires = now + persist;
        }

        cache[url] = response;

        return done(null, response);
    }

    function done(err, response) {
        return finished(err, response, xhr, params, successCallback, failureCallback, callback);
    }

}

function finished(inErr, response, xhr, params, successCallback, failureCallback, callback) {

    if (successCallback === undefined && failureCallback === undefined) { // caller is not using newer method where ajax() handles the error.
        if (callback) callback(response, xhr.status);
        return;
    }

    const { notify, err } = getErrorFromResponse(inErr, response, xhr, params);

    const result = err
        ? failureCallback && failureCallback(response, xhr, notify, next)
        : successCallback && successCallback(response, xhr, next);

    if (result !== true) { // Caller is not async
        // If caller returns true then caller is async (and they will call next() when they're ready)
        // else result will be undefined or an object of options.
        next(result);
    }

    function next(options) {
        options = (options instanceof Object && options) || {};       // Ensure is a usable object

        if (callback) callback({ success: Boolean(err), xhr, response });    // Call callback() after having returned from success()/failure()

        if (options.notify instanceof Object) {
            // options.notify could be boolean or an override object
            // If an object, then use as override options
            // and if truthy then display notification message
            // else if falsy then do not call notify()
            Object.assign(notify, options.notify);
        }

        const redirectUrl = ('redirect' in options && options.redirect)
            || (response && response.redirect);

        let notifyCall  = null;
        let location    = null;
        let reload      = false;

        const {
            title,
            text,
            value,
        } = notify;

        if (options.notify || 0 < value || (err && options.notify !== false)) {
            notifyCall = redirectUrl ? 'notifyOnVisit' : 'notify';
        }

        if (redirectUrl === window.location) { // TODO: handle when server redirects to same page
            reload = true;
        } else if (redirectUrl) {
            location = redirectUrl;
        }

        if (notifyCall) {
            window.notification[notifyCall](title, text, value);
        }

        if (location) {
            util.traceWindowLocation();
            window.location = location;
        } else if (reload) {
            util.traceWindowLocation();
            window.location.reload();
        }
    }
}

function getErrorFromResponse(err, response, xhr, params) {
    const httpStatus = xhr.status;
    const { type } = params;

    const notify = {
        title: undefined,
        text: undefined,   // Helpful user message (if possible)
        value: undefined,
    };

    const status = response && response.status;

    // Prefer JSON values if available
    if (status) {
        if (0 < status.value) {
            notify.title = 'Notice';
        } else if (status.value < 0) {
            err = true;
            notify.title = 'Error';
        }

        notify.text = status.text || '';
        notify.value = status.value || 0;

        if (notify.value === 0 && notify.text === '1805 - Save successful.') {
            notify.text = 'Your changes were saved';
        }
    }

    if (err && !notify.text) {
        Object.assign(notify, {
            title: 'Error',
            text: 'Unexpected error',
            value: -1,
        });

        // Use http(s)://.../debug/ajax/ to test combinations.

        if (xhr.status === 0) {
            notify.title = 'No server response';
            notify.text = 'Please check the network connection for errors';

        } else if (httpStatus === 403) {
            notify.title = '403 Forbidden';
            notify.text = 'Your session may have expired. Please reload the page to login and try again.';

        } else if (httpStatus !== 200) {
            notify.title = `${httpStatus} ${getReasonPhrase(httpStatus)}`;

            if (400 <= httpStatus && httpStatus < 500) {
                // 4xx Client errors.
                notify.text = 'Invalid or unexpected request. Please restart your session and try again.';

            } else if (500 <= httpStatus && httpStatus < 600) {
                // 5xx Server errors.
                notify.text = 'The application server is experiencing issues.  Please try again.';
            }

        } else if (type === 'json' && !response) { // 200 and expects JSON
            notify.text = 'Error parsing server response';
        }
    }

    if (!err && params.documentType === 'json') {
        if (!(status instanceof Object)) {
            err = true;
            Object.assign(notify, {
                title: 'Error',
                text: 'Response data missing status information',
                value: -1,
            });
        }
    }

    return { notify, err };
}

document.addEventListener('DOMContentLoaded', () => init());

function init() {

    $('[data-ajax-url]').each((idx, element) => {
        const action = element.getAttribute('data-ajax-event') || 'click';

        $(element).on(action, function (_event) {
            const url   = this.getAttribute('data-ajax-url');
            const title = this.getAttribute('data-ajax-title');

            ajax({
                url,
                method:       this.getAttribute('data-ajax-method') || 'POST',
                documentType: 'json',
                success() {
                    return title
                        ? { notify: { title } }
                        : null;
                },
            });
        });
    });
}
